import React, { ChangeEvent, Dispatch, MutableRefObject, useEffect, useRef, useState } from 'react'
import { SensorsTypes } from '../../Data'
import AlertDialog from './AlertDialog';

type DecisionDataTablePropsType = {
    data: SensorsTypes[];
    setSelectedRows: Dispatch<React.SetStateAction<SensorsTypes[]>>;
    valid: MutableRefObject<boolean>;
}

function DecisionDataTable(props: DecisionDataTablePropsType) {
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (
    title: string,
    description: string,
    yesCallback?: () => void,
    okCallback?: () => void
    ) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
    setAlertOkCallback(() => okCallback);   // Will trigger only on OK
    setAlertDialogState(true);
    };

    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [filteredData, setFilteredData] = useState<SensorsTypes[]>([])
    const [slicedData, setSlicedData] = useState<SensorsTypes[][]>([])

    const [editedCriteria, setEditedCriteria] = useState<{ [key: string]: string }>({});
    const [editedInterval, setEditedInterval] = useState<{ [key: string]: string }>({});
    const [showSave, setShowSave] = useState<{ [key: string]: boolean }>({});

    const showSaveColumn = Object.values(showSave).some(value => value === true);

    const paginationList = (data: SensorsTypes[], size: number): SensorsTypes[][] => {
        const result: SensorsTypes[][] = []
        
        if (size >= data.length) {
            return [data]
        }

        let start = 0

        while (start < data.length) {
            result.push(data.slice(start, start + size))
            start += size
        }

        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
                console.log(">>>> good next")
            }
            
        } else if (side === "prev") {
            if (0 <= selectedPage - 1){
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    // Handle slice count changed
    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    // handle search text change
    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim().toLowerCase();
        if (event.target.value === '') {
            setFilteredData(props.data)
        } else {
            setFilteredData(prevState => prevState.filter(item => (item.sensorName?.toLowerCase()?.includes(value) ?? false)))
        }
    }

    useEffect(() => {
        setFilteredData(props.data)
    }, [props.data])
    

    useEffect(() => {
        if (rowNumber.current){
            setSlicedData(prevState => paginationList(filteredData, sliceCount))
        }
    }, [sliceCount, filteredData])

    const handleCheckBoxSelected = (event: ChangeEvent<HTMLInputElement>, row: SensorsTypes) => {
            
        setSelectedCodes(prev => {
            const updated = new Set(prev);
            if (event.target.checked) {
                updated.add(event.target.name);
            } else {
                updated.delete(event.target.name);
            }
            return updated;
        })

        if (event.target.checked) {
            props.setSelectedRows(prevState => [...prevState, row])
        } else {
            props.setSelectedRows(prevState => prevState.filter(item => item.sensorId !== row.sensorId))
        }
    }

    /* const handleCriteriaChange = (event: ChangeEvent<HTMLInputElement>, row: SensorsTypes) => {
        const newCriteria = event.target.value.trim()

        // Regex to match valid pattern: "> 10", "< 5.5", etc.
        const pattern = /^(>|<)\s*(-?\d+(\.\d+)?)$/

        if (!pattern.test(newCriteria)) {
            // Invalid pattern: you can alert or just return silently
            console.warn("Invalid criteria format. Use '> number' or '< number'");
            props.valid.current = false
            return;
        } else {
            props.valid.current = true
        }

        props.setSelectedRows(prevState => 
            prevState.map(item => 
                item.sensorId === row.sensorId ? {...item, criteria: newCriteria} : item
            )
        )
    } */


        // handle criteria change
        const handleCriteriaChange = (event: ChangeEvent<HTMLInputElement>, row: SensorsTypes) => {
            const newCriteria = event.target.value;
            setEditedCriteria(prev => ({ ...prev, [row.sensorId]: newCriteria }));
            setShowSave(prev => ({ ...prev, [row.sensorId]: true }));

            props.setSelectedRows(prevState =>
                prevState.map(item =>
                    item.sensorId === row.sensorId ? { ...item, criteria: newCriteria } : item
                )
            );
        };

        // handle interval change
        const handleIntervalChange = (event: ChangeEvent<HTMLInputElement>, row: SensorsTypes) => {
            const newInterval = event.target.value;
            setEditedInterval(prev => ({ ...prev, [row.sensorId]: newInterval }));
            setShowSave(prev => ({ ...prev, [row.sensorId]: true }));

            props.setSelectedRows(prevState =>
                prevState.map(item =>
                    item.sensorId === row.sensorId ? { ...item, interval: newInterval } : item
                )
            );
        };

        /* //validate & clean only when saving
        const handleSaveCriteria = async (row: SensorsTypes) => {
            let rawCriteria = editedCriteria[row.sensorId] ?? row.criteria;

            // enforce "< number" or "> number"
            const pattern = /^(<|>)\s*(-?\d+(\.\d+)?)$/;
            if (!pattern.test(rawCriteria)) {
                alert("Invalid criteria format. Use '< 20.00' or '> 20.00'");
                return;
            }

            try {
                const response = await fetch(BASE_URL + `/iot/api/v1/criteria/${row.sensorId}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ criteria: rawCriteria.trim() }), // save as-is
                });

                if (!response.ok) {
                    throw new Error("Failed to update sensor criteria");
                }

                console.log("Criteria updated successfully");
                setShowSave(prev => ({ ...prev, [row.sensorId]: false }));
            } catch (error) {
                console.error("Error updating criteria:", error);
            }
        }; */

        // validate & save both criteria + interval
        const handleSaveRow = async (row: SensorsTypes) => {
            const rawCriteria = editedCriteria[row.sensorId] ?? row.criteria;
            const rawInterval = editedInterval[row.sensorId] ?? row.interval;
            const procId = sessionStorage.getItem("proc_id");

            // check empty criteria
            if (!rawCriteria) {
                showAlertDialog("Missing Input", "Please enter a criteria before saving.");
                return;
            }

            // check empty interval
            if (!rawInterval) {
                showAlertDialog("Missing Input", "Please enter an interval before saving.");
                return;
            }

            // validate criteria
            const pattern = /^(<|>)\s*(-?\d+(\.\d+)?)$/;
            if (!pattern.test(rawCriteria)) {
                showAlertDialog("Invalid Format", "Invalid criteria format. Use '< 20.00' or '> 20.00'");
                //alert("Invalid criteria format. Use '< 20.00' or '> 20.00'");
                return;
            }

            // validate interval (only numbers > 0)
            if (!/^\d+$/.test(rawInterval) || Number(rawInterval) <= 0) {
                showAlertDialog("Invalid Format", "Invalid interval. Enter a positive number.");
                //alert("Invalid interval. Enter a positive number.");
                return;
            }

            try {
                const response = await fetch(BASE_URL + `/iot/api/v1/sensor-details-save`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        id: {
                            procId: procId,
                            sensorId: row.sensorId,
                            criteria: rawCriteria.trim(),
                        },
                        sensorName: row.sensorName,
                        interval: rawInterval.trim(),
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to update sensor criteria & interval");
                }

                console.log("Row updated successfully");
                setShowSave(prev => ({ ...prev, [row.sensorId]: false }));
            } catch (error) {
                console.error("Error updating row:", error);
            }
        };



    return (
        <div className='subTable-wrapper'>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} defaultValue={10} min={10} onChange={(e) => handleSliceCountChange(e)}/> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here'/></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{
                        gridTemplateColumns: showSaveColumn
                            ? `3rem 1fr 1fr 1fr 1fr`
                            : `3rem 1fr 1fr 1fr`,
                        width: '100%'
                    }}>
                        <span></span>
                        <span>Sensors</span>
                        <span>Criteria</span>
                        <span>Sensor Interval(s)</span>
                        {showSaveColumn && <span></span>}
                    </div>
                </div>
                {slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                    <div key={index} className='table-wrapper' style={{alignItems: 'center', justifyContent: 'center'}}>
                        <div className='table-row' style={{
                            gridTemplateColumns: showSaveColumn
                                ? `3rem 1fr 1fr 1fr 1fr`
                                : `3rem 1fr 1fr 1fr`,
                            width: '100%'
                        }}>
                            <span style={{justifyContent: 'center'}}><input type="checkbox" name={`${row.sensorId}`} onChange={(e) => handleCheckBoxSelected(e, row)} checked={selectedCodes.has(`${row.sensorId}`)}/></span>
                            <span>{row.sensorName}</span>
                            {/* Criteria */}
                            <span>
                                <input type="text" style={{width: '100%'}} placeholder='Enter Criteria'
                                    value={editedCriteria[row.sensorId] /* ?? row.criteria */}
                                    onChange={(e) => handleCriteriaChange(e, row)} />
                            </span>

                            {/* Interval */}
                            <span>
                                <input type="text" style={{width: '100%'}} placeholder='Enter Interval'
                                    value={editedInterval[row.sensorId] /* ?? row.interval */}
                                    onChange={(e) => handleIntervalChange(e, row)} />
                            </span>

                            {/* Save button */}
                            
                            
                            <span>
                                {showSave[row.sensorId] && (
                                    <>
                                    <div className={`button-wrapper`} style={{gridArea: 'buttons', margin: '0'}}>
                                        <div className={`center-side`}>
                                            <button style={{margin: '0'}} className={`button-normal`} onClick={() => handleSaveRow(row)}>Save</button>
                                        </div>
                                    </div>
                                    {/* <button className="button-normal" onClick={() => handleSaveRow(row)}>Save</button> */}
                            
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            {filteredData.length === 0 && (
                <div className='table-no-data-wrapper'>
                    <span>no any data available</span>
                </div>
            )}
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
            {alertDialogState && (
            	<AlertDialog
                title={alertTitle}
                description={alertDescription}
                windowState={alertDialogState}
                setWindowState={setAlertDialogState}
                function={alertYesCallback}
                okFunction={alertOkCallback}
            />
            )}
        </div>
    )
}

export default DecisionDataTable